import { useEffect, useState } from "react";
import { getRenderBudget } from "@/lib/api";

/**
 * Render Budget Meter — surfaces real fal spend per user.
 *
 * Three top-line numbers: this week, this month, all-time.
 * Below that: per-provider breakdown so the user knows which model is the
 * bulk of their cost.
 *
 * Refreshes on mount and once every 30s while the component is visible —
 * cheap query, important to feel live after a render completes.
 */
export default function RenderBudgetMeter({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      getRenderBudget()
        .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
        .catch(() => { if (!cancelled) setLoading(false); });
    };
    load();
    const id = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading || !data) {
    return (
      <div
        data-testid="budget-meter-loading"
        className="border border-zinc-800 rounded-sm p-4 bg-black"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          Loading render budget…
        </div>
      </div>
    );
  }

  const stat = (label, value, count) => (
    <div className="border border-zinc-800 rounded-sm py-3 px-4 bg-black">
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
        {label}
      </div>
      <div className="font-display text-2xl font-black text-yellow-400 leading-none">
        ${value.toFixed(2)}
      </div>
      <div className="font-mono text-[10px] text-zinc-500 mt-1">
        {count} render{count === 1 ? "" : "s"}
      </div>
    </div>
  );

  return (
    <div data-testid="budget-meter" className={compact ? "" : "mb-6"}>
      <div className="flex items-baseline gap-3 mb-2">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400">
          // RENDER BUDGET
        </div>
        <div className="font-mono text-[10px] text-zinc-500">
          (real fal spend, not estimates)
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {stat("This week", data.this_week_usd, data.this_week_count)}
        {stat("This month", data.this_month_usd, data.this_month_count)}
        {stat("All time", data.all_time_usd, data.all_time_count)}
      </div>
      {Array.isArray(data.by_provider) && data.by_provider.length > 0 && (
        <div className="mt-3 border border-zinc-800 rounded-sm p-3 bg-black">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            By model
          </div>
          <div className="space-y-1">
            {data.by_provider.map((p) => (
              <div
                key={p.provider_id}
                data-testid={`budget-by-provider-${p.provider_id}`}
                className="flex items-baseline justify-between gap-3 text-xs"
              >
                <span className="text-zinc-300 truncate">{p.label}</span>
                <span className="font-mono text-zinc-400 whitespace-nowrap">
                  ${p.total_usd.toFixed(2)} · {p.renders}×
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {Array.isArray(data.recent) && data.recent.length > 0 && !compact && (
        <details className="mt-3" data-testid="budget-recent-details">
          <summary className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 cursor-pointer hover:text-yellow-400">
            ▸ Show last {data.recent.length} renders
          </summary>
          <div className="mt-2 border border-zinc-800 rounded-sm bg-black overflow-hidden">
            {data.recent.map((r) => (
              <div
                key={r.job_id}
                className="flex items-baseline justify-between gap-3 text-xs px-3 py-2 border-b border-zinc-900 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-200 truncate">{r.channel}</div>
                  <div className="font-mono text-[10px] text-zinc-500">
                    {r.provider_label} · {r.scenes_count} scenes
                    {r.fallback_clips_count > 0
                      ? ` · ${r.fallback_clips_count} fallback (free)`
                      : ""}
                  </div>
                </div>
                <div className="font-mono text-yellow-300 whitespace-nowrap">
                  ${r.estimated_cost_usd.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
